/**
 * useSearchIndexRevalidation
 *
 * Manages per-bucket search index revalidation with a 24-hour rate limit.
 *
 * - Backend is the authoritative rate-limit source (DynamoDB CACHE_TABLE, 24h TTL).
 * - localStorage is a soft client-side cache so the button is pre-disabled
 *   without needing a round-trip on mount.
 * - On success the backend returns `nextAllowedAt`; on 429 it also returns
 *   `nextAllowedAt`. Both are persisted to localStorage.
 */

import { useState, useCallback, useEffect } from 'react';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';

const LS_PREFIX = 's3portal.searchIndexNextAllowed.';

interface RevalidationState {
  isRefreshing: boolean;
  nextAllowedAt: Date | null;   // when the next revalidation is permitted
  lastSyncedAt: Date | null;    // when the last successful sync ran
}

function getStoredNextAllowed(bucketKey: string): Date | null {
  try {
    const raw = localStorage.getItem(LS_PREFIX + bucketKey);
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function storeNextAllowed(bucketKey: string, iso: string): void {
  try {
    localStorage.setItem(LS_PREFIX + bucketKey, iso);
  } catch {
    // localStorage unavailable — silent fallback
  }
}

function isWithin24h(nextAllowedAt: Date | null): boolean {
  if (!nextAllowedAt) return false;
  return nextAllowedAt.getTime() > Date.now();
}

/** Returns a human-readable "Xh Ym" string until nextAllowedAt. */
export function formatTimeUntil(nextAllowedAt: Date | null): string {
  if (!nextAllowedAt) return '';
  const msLeft = nextAllowedAt.getTime() - Date.now();
  if (msLeft <= 0) return '';
  const totalMinutes = Math.ceil(msLeft / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
}

export function useSearchIndexRevalidation(bucketKey: string) {
  const [state, setState] = useState<RevalidationState>(() => ({
    isRefreshing: false,
    nextAllowedAt: getStoredNextAllowed(bucketKey),
    lastSyncedAt: null,
  }));

  // Re-read localStorage when bucketKey changes (e.g. user switches bucket card)
  useEffect(() => {
    setState(prev => ({
      ...prev,
      nextAllowedAt: getStoredNextAllowed(bucketKey),
    }));
  }, [bucketKey]);

  const isRateLimited = isWithin24h(state.nextAllowedAt);

  const revalidate = useCallback(async (bucketName: string) => {
    if (isWithin24h(getStoredNextAllowed(bucketKey))) {
      toast.error(`Search index already refreshed. Try again in ${formatTimeUntil(getStoredNextAllowed(bucketKey))}.`);
      return;
    }

    setState(prev => ({ ...prev, isRefreshing: true }));
    try {
      const response = await apiCall(
        `/s3/revalidate-search-index/${encodeURIComponent(bucketName)}`,
        { method: 'POST' }
      );

      const nextAllowedAt = response.nextAllowedAt ? new Date(response.nextAllowedAt) : null;
      const lastSyncedAt = response.lastSyncedAt ? new Date(response.lastSyncedAt) : new Date();

      if (nextAllowedAt) storeNextAllowed(bucketKey, response.nextAllowedAt);

      setState(prev => ({ ...prev, isRefreshing: false, nextAllowedAt, lastSyncedAt }));
      toast.success(`Search index refreshed for ${bucketName}`);
    } catch (err: any) {
      setState(prev => ({ ...prev, isRefreshing: false }));

      // 429 — backend rate limit hit; persist nextAllowedAt from response body
      if (err.status === 429 || (err.message && err.message.includes('already revalidated'))) {
        try {
          const body = typeof err.body === 'object' ? err.body : JSON.parse(err.body || '{}');
          if (body.nextAllowedAt) {
            storeNextAllowed(bucketKey, body.nextAllowedAt);
            setState(prev => ({ ...prev, nextAllowedAt: new Date(body.nextAllowedAt) }));
          }
        } catch {
          // ignore parse errors
        }
        toast.error(err.message || 'Search index already refreshed today.');
      } else {
        toast.error(err.message || 'Failed to refresh search index');
      }
    }
  }, [bucketKey]);

  return {
    isRefreshing: state.isRefreshing,
    isRateLimited,
    nextAllowedAt: state.nextAllowedAt,
    lastSyncedAt: state.lastSyncedAt,
    revalidate,
    timeUntilLabel: formatTimeUntil(state.nextAllowedAt),
  };
}
